import { useEffect, useState } from "react";

const getToken = (role) => {
  // Same portal-specific token precedence as useGovernedAdvertiserCatalog.
  const keys = role === "superadmin"
    ? ["superadminToken", "authToken", "token", "accessToken"]
    : ["clientAuthToken", "authToken", "token", "accessToken"];
  return keys.map((key) => localStorage.getItem(key)).find((value) => value?.trim()) || "";
};
const initial = (scope) => ({ scope, phase: "loading", status: null, report: null, mapping: null, error: "" });

export function validateYahooSavedReport(report) {
  if (!report || typeof report.available !== "boolean") throw new Error("Yahoo saved reporting is unavailable.");
  if (!report.available) return report;
  if (!Array.isArray(report.rows) || !report.timezone || !/^[A-Z]{3}$/.test(report.currency || "") ||
      !/^\d{4}-\d{2}-\d{2}$/.test(report.dateStart || "") || !/^\d{4}-\d{2}-\d{2}$/.test(report.dateEnd || "") ||
      !Number.isFinite(Date.parse(report.fetchedAt)) ||
      report.rows.some((row) => !/^\d{4}-\d{2}-\d{2}$/.test(row.date || "") || row.currency !== report.currency || row.timezone !== report.timezone) ||
      (!report.rows.length && report.job?.rowCount !== 0)) {
    throw new Error("Yahoo saved reporting is incomplete. Refresh the saved evidence or check the connection.");
  }
  return report;
}

// Only saved backend evidence is fetched. This hook cannot authenticate with
// Yahoo, create a report job, ingest data or perform any provider mutation.
export default function useYahooDspReporting({ apiBase, advertiserId, role }) {
  const [mappingId, setMappingId] = useState("");
  const [revision, setRevision] = useState(0);
  const scope = JSON.stringify([apiBase, advertiserId, mappingId, revision]);
  const [state, setState] = useState(() => initial(scope));
  useEffect(() => {
    const controller = new AbortController();
    let active = true;
    const next = (patch) => { if (active) setState({ ...initial(scope), ...patch }); };
    next({});
    const base = `${apiBase}/api/yahoo-dsp/${encodeURIComponent(advertiserId)}`;
    async function request(path) {
      const response = await fetch(`${base}${path}`, { signal: controller.signal, cache: "no-store",
        headers: { Authorization: `Bearer ${getToken(role)}` } });
      if (!response.ok) throw new Error(response.status === 401 || response.status === 403
        ? "Yahoo reporting access is unavailable for this advertiser. Check the current account authorization."
        : "Yahoo saved reporting is unavailable. Try refreshing the saved evidence.");
      const result = await response.json();
      if (result?.ok === false) throw new Error("Yahoo saved reporting is unavailable. Check the connection.");
      return result;
    }
    async function load() {
      if (!advertiserId) { next({ phase: "advertiser_required" }); return; }
      try {
        const status = await request("/status");
        if (!active) return;
        if (typeof status.configured !== "boolean" || !Array.isArray(status.mappings)) throw new Error("Yahoo readiness is unavailable.");
        const mapping = status.mappings.find((item) => String(item.id) === mappingId) || null;
        const context = { status, mapping };
        if (!status.configured) { next({ ...context, phase: "disconnected" }); return; }
        if (!status.mappings.length) { next({ ...context, phase: "unmapped" }); return; }
        if (!mappingId) { next({ ...context, phase: "mapping_required" }); return; }
        if (!mapping) { next({ ...context, phase: "mapping_unavailable" }); return; }
        if (!mapping.accessVerified) { next({ ...context, phase: "access_unverified" }); return; }
        if (!mapping.reportingVerified) { next({ ...context, phase: "reporting_unverified" }); return; }
        next({ ...context, phase: "loading" });
        const report = validateYahooSavedReport(await request(`/${encodeURIComponent(mappingId)}/reporting`));
        if (!report.available) { next({ ...context, phase: "reporting_unverified" }); return; }
        next({ ...context, report, phase: report.rows.length ? "ready" : "empty" });
      } catch (error) {
        // Discard affirmative readiness and all report data on an error. A
        // previous successful response must never survive a failed refresh.
        if (error.name !== "AbortError") next({ phase: "unavailable", error: error.message });
      }
    }
    load();
    return () => { active = false; controller.abort(); };
  }, [apiBase, advertiserId, role, mappingId, scope]);
  // Mapping changes clear visible data synchronously, before effects run.
  const current = state.scope === scope ? state : initial(scope);
  return { ...current, mappingId, selectMapping: setMappingId, refresh: () => setRevision((value) => value + 1) };
}
